import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

type ReviewTab = 'answer' | 'solution';

interface ReviewAnswerSolutionTabsProps {
  answer: React.ReactNode;
  solution: React.ReactNode;
  hasSolution?: boolean;
  /** When this changes (e.g. a new question), the tabs reset to "Answer". */
  resetKey?: string | number;
}

export const ReviewAnswerSolutionTabs = ({
  answer,
  solution,
  hasSolution = true,
  resetKey,
}: ReviewAnswerSolutionTabsProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ReviewTab>('answer');

  useEffect(() => {
    setActiveTab('answer');
  }, [resetKey]);

  const tabs: { id: ReviewTab; label: string }[] = [
    { id: 'answer', label: t('PAL_AR_TAB_ANSWER') },
    ...(hasSolution
      ? [{ id: 'solution' as ReviewTab, label: t('PAL_AR_TAB_SOLUTION') }]
      : []),
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: '6px',
          mb: '16px',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <Box
              component="button"
              type="button"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              sx={{
                appearance: 'none',
                background: 'none',
                border: 'none',
                borderBottom: '2.5px solid transparent',
                mb: '-1px',
                px: '14px',
                py: '8px',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                color: '#64748b',
                cursor: 'pointer',
                transition: 'color 0.15s ease, border-color 0.15s ease',
                '&:hover': { color: '#475569' },
                ...(isActive && {
                  color: '#6366f1',
                  borderBottomColor: '#6366f1',
                }),
              }}
            >
              {tab.label}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: activeTab === 'answer' ? 'block' : 'none' }}>
        {answer}
      </Box>
      {hasSolution && (
        <Box sx={{ display: activeTab === 'solution' ? 'block' : 'none' }}>
          {solution}
        </Box>
      )}
    </Box>
  );
};
